import { gql } from '@apollo/client/core'
import { useQuery } from '@meesayen/apollo-composable'
import { computed, reactive, watchEffect } from 'vue'
import {
  projectThingBookmarkDataFragment,
  projectThingFragment,
} from '/@/common/projects/api/fragments'
import { useRollbar } from '/@/core/rollbar'
import type {
  ProjectGenericThing,
  ProjectProduct,
  ProjectThingType,
} from '/@/common/things/types'
import type {
  ProjectThingBudget,
  ProjectThingBudgetStatus,
} from '/@/common/budget/types'
import { categoryFragment } from '/@/common/categories/fragments'
import { projectThingBudgetStatusChangeFragment } from '/@/common/budget/fragments'
import type { Project } from '../projects/types'
import { extractPaginatedData, type PaginatedList } from '/@/utils/pagination'
import type { FilterKey, FilterValue } from '../discover/types'

interface CartFilters {
  projectIds: string[]
  vendorIds: string[]
  searchText?: string
  projectThingTypes: ProjectThingType[]
  statuses: ProjectThingBudgetStatus[]
  first?: number
  after?: string
}

const filters = reactive<CartFilters>({
  projectIds: [],
  vendorIds: [],
  searchText: undefined,
  projectThingTypes: [],
  statuses: [],
  first: 15,
  after: undefined,
})

export function useCartFilters() {
  return {
    filters,
    reset() {
      filters.projectIds = []
      filters.vendorIds = []
      filters.projectThingTypes = []
      filters.statuses = []
      filters.searchText = undefined
      filters.first = undefined
      filters.after = undefined
    },
  }
}

interface CartItemProjectData {
  projectId: string
  project: Pick<Project, 'id' | 'name' | 'projectType'>
}
export type CartItem = Omit<ProjectThingBudget, 'statusChanges'> & {
  id: string
  projectThing:
    | (Omit<ProjectProduct, 'thingBudget'> & CartItemProjectData)
    | (Omit<ProjectGenericThing, 'thingBudget'> & CartItemProjectData)
}

interface BudgetThingsMetaData {
  filters: Array<{
    key: FilterKey
    label: string
    values: FilterValue[]
    isGrouped: boolean
    isGroupSelectable: boolean
  }>
  subtotal: { subtotal: string; subtotalCents: number }
  statuses: Array<{
    status: ProjectThingBudgetStatus
    projectThingType: ProjectThingType
    count: number
  }>
}

interface CartData {
  budgetThings: PaginatedList<CartItem>
}

export function useCartData() {
  const rollbar = useRollbar()
  const { result: metadataResult } = useQuery<{
    budgetThingsMetadata: BudgetThingsMetaData
  }>(
    gql`
      query getGlobalCartMetadata_Cart(
        $projectIds: [String!]
        $projectThingTypes: [ProjectThingType!]
        $statuses: [ProjectThingBudgetStatus!]
        $vendorIds: [String!]
        $searchText: String
      ) {
        budgetThingsMetadata(
          projectIds: $projectIds
          projectThingTypes: $projectThingTypes
          statuses: $statuses
          vendorIds: $vendorIds
          searchText: $searchText
        ) {
          subtotal {
            subtotal
            subtotalCents
          }
          statuses {
            status
            projectThingType
            count
          }
          filters {
            key
            label
            values {
              value
              label
              count
              group
            }
            isGrouped
            isGroupSelectable
          }
        }
      }
    `,
    () => ({
      projectIds: filters.projectIds,
      projectThingTypes: filters.projectThingTypes,
      statuses: filters.statuses,
      searchText: filters.searchText,
      vendorIds: filters.vendorIds,
    }),
  )

  const { result, loading, error, fetchMore } = useQuery<CartData>(
    gql`
      ${projectThingFragment}
      ${projectThingBookmarkDataFragment}
      ${projectThingBudgetStatusChangeFragment}
      ${categoryFragment}

      query getGlobalCartData_Cart(
        $projectIds: [String!]
        $vendorIds: [String!]
        $projectThingTypes: [ProjectThingType!]
        $statuses: [ProjectThingBudgetStatus!]
        $searchText: String
        $first: Int
        $after: String
      ) {
        budgetThings(
          projectIds: $projectIds
          vendorIds: $vendorIds
          projectThingTypes: $projectThingTypes
          statuses: $statuses
          searchText: $searchText
          first: $first
          after: $after
        ) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            cursor
            node {
              id
              price
              priceCents
              quantity
              shipping
              shippingCents
              total
              totalCents
              tax
              taxCents
              taxPercentage
              markup
              markupCents
              markupPercentage
              discount
              discountCents
              discountPercentage
              orderNumber
              trackingInfo
              leadTime
              estimatedDeliveryAt
              status {
                ...ProjectThingBudgetStatusChangeProps
              }

              projectThing {
                ...ProjectThingCommonProps

                projectId
                project {
                  id
                  name
                  projectType
                }

                ... on ProjectProduct {
                  name
                  note
                  imageUrl
                  thing {
                    ...ProjectThingThingProps
                  }
                  product {
                    name
                    minPrice
                    maxPrice
                    minPriceCent
                    maxPriceCent
                    description
                    categoryKey
                    category {
                      ...CategoryProps
                    }
                    vendor {
                      id
                      name
                      isVerified
                    }
                    bookmarkInfo {
                      id
                    }
                    vendorUrl
                    cleanVendorUrl
                    id
                    thingType
                    editable
                    isVerified
                    addedByDesignerId
                    hasPriceAlert
                  }
                  url
                  cleanUrl
                  affiliateUrl
                  vendor {
                    id
                    name
                    isVerified
                  }
                  price
                  category {
                    ...CategoryProps
                  }
                }

                ... on ProjectGenericThing {
                  name
                  vendor {
                    id
                    name
                    isVerified
                  }
                  note
                  thingType
                  images {
                    id
                    url
                  }
                  imageUrl
                  category {
                    ...CategoryProps
                  }
                  projectGenericThingSource
                }
              }
            }
          }
        }
      }
    `,
    () => ({
      projectIds: filters.projectIds,
      vendorIds: filters.vendorIds,
      searchText: filters.searchText,
      projectThingTypes: filters.projectThingTypes,
      statuses: filters.statuses,
      first: filters.first,
      after: filters.after,
    }),
    { notifyOnNetworkStatusChange: true },
  )

  watchEffect(() => {
    if (error.value) {
      rollbar.error(error.value)
    }
  })

  const items = computed(() => extractPaginatedData(result.value?.budgetThings))

  const groupedItems = computed(() => {
    const projectOrders: Set<string> = new Set()
    const groups: Map<
      string,
      { project: CartItem['projectThing']['project']; items: CartItem[] }
    > = new Map()
    for (const item of items.value) {
      if (!groups.has(item.projectThing.projectId)) {
        projectOrders.add(item.projectThing.projectId)
        groups.set(item.projectThing.projectId, {
          project: item.projectThing.project,
          items: [],
        })
      }
      groups.get(item.projectThing.projectId)?.items.push(item)
    }

    return {
      orders: projectOrders,
      groups,
    }
  })

  return {
    loading,
    metadata: computed(() => metadataResult.value?.budgetThingsMetadata),
    pageInfo: computed(() => result.value?.budgetThings.pageInfo),
    items,
    groupedItems,
    fetchMore() {
      const pi = result.value?.budgetThings.pageInfo
      if (!pi?.hasNextPage) return
      fetchMore({
        variables: {
          after: pi.endCursor,
        },
      })
    },
  }
}
